import { isChecklistPlaceholderLabel } from './checklist-unresolved';
/**
 * Pure source-snapshot and preview-diff primitives for external checklist data.
 *
 * Azure/database I/O stays outside this module. Callers provide source rows that
 * have already been read, then receive a deterministic, target-scoped snapshot.
 */

import type {
  ChecklistParallelDefinition,
  ParallelApplicability,
  ParallelApplicabilityConfidence,
} from './checklist-parallel';

export interface ChecklistSourceProduct {
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  category: string;
  year: string;
  brand: string;
  product: string;
  brandTitleSafe?: string;
  productTitleSafe?: string;
  manufacturer?: string;
  categoryType?: ChecklistSourceCategoryType;
  franchise?: string;
  tvShow?: string;
  movie?: string;
  genre?: string;
  game?: string;
  marketplaceAspects?: Record<string, string>;
}

export interface ChecklistSourceUpload {
  schemaVersion: ChecklistSourceSchemaVersion;
  extractedAt: string;
  sourceHighWater?: string;
  product: ChecklistSourceProduct;
  rows: AzureChecklistRosterRow[];
  expectedRosterCount?: number;
  parallelDefinitions?: AzureChecklistParallelRow[];
  expectedParallelDefinitionCount?: number;
  subsetDefinitions?: AzureChecklistSubsetRow[];
  expectedSubsetDefinitionCount?: number;
}

export interface AzureChecklistRosterRow {
  sourceTargetId: string;
  sourceItemId: string;
  sourceSubsetId: string;
  subset: string;
  subsetTitleSafe?: string;
  number: string;
  name: string;
  nameTitleSafe?: string;
  team?: string;
  sourceFile?: string;
  autographAuthentication?: string;
  autographAuthenticationNumber?: string;
  autographFormat?: string;
  marketplaceAspects?: Record<string, string>;
  rarity?: string;
  finish?: string;
  illustrator?: string;
  featuredPerson?: string;
  eventTournament?: string;
  ccgAttributes?: Record<string, unknown>;
  ccgAttributesRaw?: string;
  parallelName?: string;
  parallelNameTitleSafe?: string;
  serialTo?: number;
  cardType?: string;
  players?: string[];
  notes?: string;
  rookie?: boolean;
  shortPrint?: boolean;
  shortPrintNote?: string;
  autograph?: boolean;
  memorabilia?: boolean;
  sourceFamilyId?: string;
}

export type ChecklistMarketplaceCategoryType = 'non_sport' | 'sport';
export type ChecklistSourceCategoryType = ChecklistMarketplaceCategoryType | 'ccg';
export type ChecklistSourceSchemaVersion = 6 | 7;

export interface ChecklistMarketplaceProfile {
  marketplace: 'EBAY_US';
  inventoryType: 'SINGLE';
  categoryType: ChecklistMarketplaceCategoryType;
  categoryId: '183050' | '261328';
  aspects: Record<string, string>;
}

export interface AzureChecklistParallelRow {
  sourceTargetId: string;
  sourceSubsetId: string;
  name: string;
  nameTitleSafe?: string;
  serialTo?: number;
  printRunText?: string;
  applicability: ParallelApplicability;
  applicabilityConfidence: ParallelApplicabilityConfidence;
  autograph: boolean;
  memorabilia: boolean;
  shortPrint: boolean;
  sourceFile?: string;
  notes?: string;
  sourceFamilyId?: string;
}

export interface AzureChecklistSubsetRow {
  sourceTargetId: string;
  sourceSubsetId: string;
  name: string;
  nameTitleSafe?: string;
  serialTo?: number;
  printRunText?: string;
  applicabilityConfidence: ParallelApplicabilityConfidence;
  autograph: boolean;
  memorabilia: boolean;
  shortPrint: boolean;
  /** Omitted/false permits a plain version; only true changes canonical content. */
  noPlainVersion?: boolean;
  sourceFile?: string;
  notes?: string;
  sourceFamilyId?: string;
}

export interface ChecklistSubsetDefinition {
  definitionId: string;
  name: string;
  nameTitleSafe?: string;
  serialTo?: number;
  printRunText?: string;
  applicabilityConfidence: ParallelApplicabilityConfidence;
  autograph: boolean;
  memorabilia: boolean;
  shortPrint: boolean;
  /** Omitted/false permits a plain version; only true changes canonical content. */
  noPlainVersion?: boolean;
  notes?: string;
  source: {
    system: 'azure-sql-ebay';
    targetId: string;
    subsetId: string;
    snapshotFingerprint: string;
    familyId?: string;
    sourceFile?: string;
  };
}

export interface ChecklistRosterSnapshotEntry {
  naturalKey: string;
  subset: string;
  subsetTitleSafe?: string;
  number: string;
  name: string;
  nameTitleSafe?: string;
  team?: string;
  sourceItemId: string;
  sourceSubsetId: string;
  sourceFile?: string;
  marketplaceAspects?: Record<string, string>;
  rarity?: string;
  finish?: string;
  autographAuthentication?: string;
  autographAuthenticationNumber?: string;
  autographFormat?: string;
  illustrator?: string;
  featuredPerson?: string;
  eventTournament?: string;
  ccgAttributes?: Record<string, unknown>;
  ccgAttributesRaw?: string;
  parallelName?: string;
  parallelNameTitleSafe?: string;
  serialTo?: number;
  cardType?: string;
  players?: string[];
  notes?: string;
  rookie: boolean;
  shortPrint: boolean;
  shortPrintNote?: string;
  autograph: boolean;
  memorabilia: boolean;
  sourceFamilyId?: string;
}

export interface ChecklistSourceSnapshot {
  schemaVersion: ChecklistSourceSchemaVersion;
  extractedAt?: string;
  sourceHighWater?: string;
  sourceSystem: 'azure-sql-ebay';
  sourceTargetId: string;
  category: string;
  year: string;
  brand: string;
  product: string;
  sourceBrand?: string;
  sourceBrandTitleSafe?: string;
  productTitleSafe?: string;
  manufacturer?: string;
  sourceCategoryType?: ChecklistSourceCategoryType;
  franchise?: string;
  tvShow?: string;
  movie?: string;
  genre?: string;
  game?: string;
  fingerprint: string;
  rosterCount: number;
  roster: ChecklistRosterSnapshotEntry[];
  parallelDefinitionCount: number;
  parallelDefinitions: ChecklistParallelDefinition[];
  subsetDefinitionCount: number;
  subsetDefinitions: ChecklistSubsetDefinition[];
  marketplace?: ChecklistMarketplaceProfile;
}

export const CHECKLIST_IMPORTS_PK = 'CHECKLIST_IMPORTS';

export function checklistSourceRecordsPk(sourceSystem: string, sourceTargetId: string): string {
  return `CHECKLIST_SOURCE#${clean(sourceSystem)}#${clean(sourceTargetId)}`;
}

/** Stable v7 provenance key for one exact source roster item. */
export function checklistSourceItemSortKey(sourceItemId: string): string {
  const itemId = clean(sourceItemId);
  if (itemId === '') throw new Error('checklist source item id is required');
  return `SOURCE_ITEM#${itemId}`;
}

export function checklistParallelDefinitionsPk(
  sourceSystem: string,
  sourceTargetId: string,
): string {
  return `CHECKLIST_PARALLELS#${clean(sourceSystem)}#${clean(sourceTargetId)}`;
}

export function checklistSubsetDefinitionsPk(sourceSystem: string, sourceTargetId: string): string {
  return `CHECKLIST_SUBSETS#${clean(sourceSystem)}#${clean(sourceTargetId)}`;
}

export function checklistSourceProductProfilePk(
  sourceSystem: string,
  sourceTargetId: string,
): string {
  return `CHECKLIST_SOURCE_PRODUCT#${clean(sourceSystem)}#${clean(sourceTargetId)}`;
}

/**
 * Stable product/checklist identity for an external source target.
 *
 * Taxonomy is mutable display/search metadata and is therefore deliberately
 * excluded. Two source targets with the same year/brand/product remain
 * independent checklist partitions.
 */
export function checklistSourceProductId(sourceSystem: string, sourceTargetId: string): string {
  const system = clean(sourceSystem);
  const targetId = clean(sourceTargetId);
  if (system === '' || targetId === '') {
    throw new Error('checklist source system and target id are required');
  }
  return `SOURCE#${system}#${targetId}`;
}

export function parseChecklistSourceProductId(
  checklistId: string,
): { system: 'azure-sql-ebay'; targetId: string } | undefined {
  const match = /^SOURCE#([^#]+)#([^#]+)$/.exec(checklistId);
  if (match === null) return undefined;
  const system = clean(match[1] ?? '');
  const targetId = clean(match[2] ?? '');
  return system !== 'azure-sql-ebay' || targetId === ''
    ? undefined
    : { system: 'azure-sql-ebay', targetId };
}

export interface ChecklistSnapshotBuildOptions {
  expectedTargetId: string;
  expectedRosterCount?: number;
  expectedParallelDefinitionCount?: number;
  parallelDefinitions?: readonly AzureChecklistParallelRow[];
  expectedSubsetDefinitionCount?: number;
  subsetDefinitions?: readonly AzureChecklistSubsetRow[];
  extractedAt?: string;
  sourceHighWater?: string;
  schemaVersion?: ChecklistSourceSchemaVersion;
}

export type ChecklistSnapshotBuildErrorCode =
  | 'invalid-source-snapshot'
  | 'invalid-product'
  | 'mixed-target'
  | 'missing-required-field'
  | 'invalid-roster-attribute'
  | 'duplicate-source-item-id'
  | 'duplicate-natural-key'
  | 'unexpected-roster-count'
  | 'invalid-parallel-definition'
  | 'duplicate-parallel-definition'
  | 'unexpected-parallel-definition-count'
  | 'invalid-subset-definition'
  | 'duplicate-subset-definition'
  | 'unexpected-subset-definition-count'
  | 'unresolved-subset-definition'
  | 'misclassified-subset-definition';

export interface ChecklistSnapshotBuildError {
  code: ChecklistSnapshotBuildErrorCode;
  detail: string;
  sourceItemIds?: string[];
  /** JSON Pointer into the original upload; never the sorted normalized snapshot. */
  fieldPath?: string;
  sourceTargetId?: string;
  sourceItemId?: string;
  sourceSubsetId?: string;
  definitionId?: string;
}

export type ChecklistSnapshotBuildWarningCode = 'missing-card-number' | 'missing-card-name';

export interface ChecklistSnapshotBuildWarning {
  code: ChecklistSnapshotBuildWarningCode;
  detail: string;
  sourceItemId: string;
}

export type ChecklistSnapshotBuildResult =
  | {
      ok: true;
      snapshot: ChecklistSourceSnapshot;
      warnings: ChecklistSnapshotBuildWarning[];
    }
  | { ok: false; errors: ChecklistSnapshotBuildError[] };

export type ChecklistSnapshotDiffKind = 'add' | 'update' | 'unchanged' | 'source-missing';

export interface ChecklistSnapshotDiffItem {
  kind: ChecklistSnapshotDiffKind;
  naturalKey: string;
  before?: ChecklistRosterSnapshotEntry;
  after?: ChecklistRosterSnapshotEntry;
}

export interface ChecklistSnapshotDiff {
  adds: ChecklistSnapshotDiffItem[];
  updates: ChecklistSnapshotDiffItem[];
  unchanged: ChecklistSnapshotDiffItem[];
  sourceMissing: ChecklistSnapshotDiffItem[];
}

export interface ChecklistParallelDefinitionDiffItem {
  kind: ChecklistSnapshotDiffKind;
  definitionId: string;
  before?: ChecklistParallelDefinition;
  after?: ChecklistParallelDefinition;
}

export interface ChecklistParallelDefinitionDiff {
  adds: ChecklistParallelDefinitionDiffItem[];
  updates: ChecklistParallelDefinitionDiffItem[];
  unchanged: ChecklistParallelDefinitionDiffItem[];
  sourceMissing: ChecklistParallelDefinitionDiffItem[];
}

export interface ChecklistSubsetDefinitionDiffItem {
  kind: ChecklistSnapshotDiffKind;
  definitionId: string;
  before?: ChecklistSubsetDefinition;
  after?: ChecklistSubsetDefinition;
}

export interface ChecklistSubsetDefinitionDiff {
  adds: ChecklistSubsetDefinitionDiffItem[];
  updates: ChecklistSubsetDefinitionDiffItem[];
  unchanged: ChecklistSubsetDefinitionDiffItem[];
  sourceMissing: ChecklistSubsetDefinitionDiffItem[];
}

function clean(value: string | undefined): string {
  return (value ?? '').trim();
}

function canonicalSerialTo(value: number | undefined, schemaVersion: number): number | undefined {
  return schemaVersion >= 7 && value === 0 ? undefined : value;
}

function fold(value: string): string {
  return clean(value).toLocaleLowerCase('en-US');
}

function segment(value: string): string {
  return `${value.length}:${value}`;
}

export function checklistRosterNaturalKey(
  subset: string,
  number: string,
  sourceItemId?: string,
): string {
  return `${segment(fold(subset))}${segment(fold(number))}${
    clean(sourceItemId) === '' ? '' : segment(clean(sourceItemId))
  }`;
}

function canonicalEntry(entry: ChecklistRosterSnapshotEntry): string {
  const values = [
    entry.naturalKey,
    entry.subset,
    entry.number,
    entry.name,
    entry.team ?? '',
    entry.sourceItemId,
    entry.sourceSubsetId,
    entry.sourceFile ?? '',
  ];
  values.push(
    entry.rarity ?? '',
    entry.finish ?? '',
    entry.autographAuthentication ?? '',
    entry.autographAuthenticationNumber ?? '',
    entry.autographFormat ?? '',
    entry.illustrator ?? '',
    entry.featuredPerson ?? '',
    entry.eventTournament ?? '',
    canonicalUnknownMap(entry.ccgAttributes),
    entry.ccgAttributesRaw ?? '',
    entry.parallelName ?? '',
    entry.serialTo?.toString() ?? '',
    entry.cardType ?? '',
    entry.notes ?? '',
    entry.rookie ? '1' : '0',
    entry.shortPrint ? '1' : '0',
    entry.shortPrintNote ?? '',
    entry.autograph ? '1' : '0',
    entry.memorabilia ? '1' : '0',
    entry.sourceFamilyId ?? '',
    ...(entry.players ?? []),
  );
  if (entry.marketplaceAspects !== undefined) {
    values.push(canonicalStringMap(entry.marketplaceAspects));
  }
  if (
    entry.subsetTitleSafe !== undefined ||
    entry.nameTitleSafe !== undefined ||
    entry.parallelNameTitleSafe !== undefined
  ) {
    values.push(
      'schema-v7-title-safe',
      entry.subsetTitleSafe ?? '',
      entry.nameTitleSafe ?? '',
      entry.parallelNameTitleSafe ?? '',
    );
  }
  return values.map(segment).join('');
}

function canonicalUnknownMap(value: Readonly<Record<string, unknown>> | undefined): string {
  if (value === undefined) return '';
  return JSON.stringify(
    Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right))),
  );
}

function canonicalStringMap(value: Readonly<Record<string, string>> | undefined): string {
  if (value === undefined) return '';
  return Object.entries(value)
    .map(([key, entry]) => [clean(key), clean(entry)] as const)
    .filter(([key, entry]) => key !== '' && entry !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .flatMap(([key, entry]) => [key, entry])
    .map(segment)
    .join('');
}

function normalizeStringMap(
  value: Readonly<Record<string, string>> | undefined,
): Record<string, string> | undefined {
  if (value === undefined) return undefined;
  const normalized = Object.fromEntries(
    Object.entries(value)
      .map(([key, entry]) => [clean(key), clean(entry)] as const)
      .filter(([key, entry]) => key !== '' && entry !== '')
      .sort(([left], [right]) => left.localeCompare(right)),
  );
  return Object.keys(normalized).length === 0 ? undefined : normalized;
}

function marketplaceProfile(
  product: ChecklistSourceProduct,
): ChecklistMarketplaceProfile | undefined {
  if (product.categoryType !== 'non_sport' && product.categoryType !== 'sport') return undefined;
  const aspects = normalizeStringMap({
    ...(product.marketplaceAspects ?? {}),
    ...(clean(product.franchise) === '' ? {} : { Franchise: clean(product.franchise) }),
    ...(clean(product.tvShow) === '' ? {} : { 'TV Show': clean(product.tvShow) }),
  });
  return {
    marketplace: 'EBAY_US',
    inventoryType: 'SINGLE',
    categoryType: product.categoryType,
    categoryId: product.categoryType === 'non_sport' ? '183050' : '261328',
    aspects: aspects ?? {},
  };
}

function canonicalApplicability(value: ParallelApplicability): string {
  if (value.kind === 'explicit-cards') {
    return [value.kind, value.subset, ...value.numbers.map(fold).sort()].map(segment).join('');
  }
  return [value.kind, value.subset ?? ''].map(segment).join('');
}

function canonicalParallelDefinition(definition: ChecklistParallelDefinition): string {
  const values = [
    definition.definitionId,
    definition.name,
    definition.serialTo?.toString() ?? '',
    definition.printRunText ?? '',
    canonicalApplicability(definition.applicability),
    definition.applicabilityConfidence,
    definition.autograph ? '1' : '0',
    definition.memorabilia ? '1' : '0',
    definition.shortPrint ? '1' : '0',
    definition.notes ?? '',
    definition.source.system,
    definition.source.targetId,
    definition.source.subsetId,
    definition.source.sourceFile ?? '',
  ];
  values.push(definition.source.familyId ?? '');
  if (definition.nameTitleSafe !== undefined) {
    values.push('schema-v7-title-safe', definition.nameTitleSafe);
  }
  return values.map(segment).join('');
}

function canonicalSubsetDefinition(definition: ChecklistSubsetDefinition): string {
  const values = [
    definition.definitionId,
    definition.name,
    definition.serialTo?.toString() ?? '',
    definition.printRunText ?? '',
    definition.applicabilityConfidence,
    definition.autograph ? '1' : '0',
    definition.memorabilia ? '1' : '0',
    definition.shortPrint ? '1' : '0',
    definition.notes ?? '',
    definition.source.system,
    definition.source.targetId,
    definition.source.subsetId,
    definition.source.sourceFile ?? '',
    definition.source.familyId ?? '',
  ];
  if (definition.nameTitleSafe !== undefined) {
    values.push('schema-v7-title-safe', definition.nameTitleSafe);
  }
  if (definition.noPlainVersion === true) {
    values.push('schema-v7-no-plain-version', '1');
  }
  return values.map(segment).join('');
}

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x00000100000001b3n;
const FNV_MASK = 0xffffffffffffffffn;

function fingerprint(input: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= BigInt(input.charCodeAt(i));
    hash = (hash * FNV_PRIME) & FNV_MASK;
  }
  return hash.toString(16).padStart(16, '0');
}

function validateProduct(
  product: ChecklistSourceProduct,
  expectedTargetId: string,
): ChecklistSnapshotBuildError[] {
  const errors: ChecklistSnapshotBuildError[] = [];
  const required: [string, string][] = [
    ['sourceTargetId', product.sourceTargetId],
    ['category', product.category],
    ['year', product.year],
    ['brand', product.brand],
    ['product', product.product],
  ];
  for (const [field, value] of required) {
    if (clean(value) === '') {
      errors.push({
        code: 'invalid-product',
        detail: 'A required product field is empty.',
        fieldPath: `/product/${field}`,
      });
    }
  }
  if (
    clean(product.sourceTargetId) !== '' &&
    clean(product.sourceTargetId) !== clean(expectedTargetId)
  ) {
    errors.push({
      code: 'mixed-target',
      detail: 'The product target does not match the expected target.',
      fieldPath: '/product/sourceTargetId',
    });
  }
  return errors;
}

export function buildChecklistSourceSnapshot(
  product: ChecklistSourceProduct,
  rows: readonly AzureChecklistRosterRow[],
  options: ChecklistSnapshotBuildOptions,
): ChecklistSnapshotBuildResult {
  const errors = validateProduct(product, options.expectedTargetId);
  const schemaVersion = options.schemaVersion ?? 7;
  const sourceBrand = clean(product.brand);
  const sourceBrandTitleSafe = clean(product.brandTitleSafe);
  const productTitleSafe = clean(product.productTitleSafe);
  const manufacturer = clean(product.manufacturer);
  const canonicalBrand = manufacturer !== '' ? manufacturer : sourceBrand;
  const entries: ChecklistRosterSnapshotEntry[] = [];
  const originalRowIndexes = new Map<ChecklistRosterSnapshotEntry, number>();
  const entriesByKey = new Map<string, ChecklistRosterSnapshotEntry[]>();
  const entriesBySourceItemId = new Map<string, ChecklistRosterSnapshotEntry[]>();
  const definitions: ChecklistParallelDefinition[] = [];
  const definitionsById = new Map<string, ChecklistParallelDefinition[]>();
  const subsetDefinitions: ChecklistSubsetDefinition[] = [];
  const subsetDefinitionsById = new Map<string, ChecklistSubsetDefinition[]>();
  const warnings: ChecklistSnapshotBuildWarning[] = [];

  for (const [rowIndex, row] of rows.entries()) {
    const sourceTargetId = clean(row.sourceTargetId);
    const sourceItemId = clean(row.sourceItemId);
    const sourceSubsetId = clean(row.sourceSubsetId);
    const subset = clean(row.subset);
    const subsetTitleSafe = clean(row.subsetTitleSafe);
    const number = clean(row.number);
    const name = clean(row.name);
    const nameTitleSafe = clean(row.nameTitleSafe);
    const team = clean(row.team);
    const sourceFile = clean(row.sourceFile);
    const rarity = clean(row.rarity);
    const finish = clean(row.finish);
    const autographAuthentication = clean(row.autographAuthentication);
    const autographAuthenticationNumber = clean(row.autographAuthenticationNumber);
    const autographFormat = clean(row.autographFormat);
    const illustrator = clean(row.illustrator);
    const featuredPerson = clean(row.featuredPerson);
    const eventTournament = clean(row.eventTournament);
    const ccgAttributesRaw = clean(row.ccgAttributesRaw);
    const parallelName = clean(row.parallelName);
    const parallelNameTitleSafe = clean(row.parallelNameTitleSafe);
    const serialTo = canonicalSerialTo(row.serialTo, schemaVersion);
    const cardType = clean(row.cardType);
    const players = row.players?.map((player) => clean(player)).filter((player) => player !== '');
    const notes = clean(row.notes);
    const shortPrintNote = clean(row.shortPrintNote);
    const sourceFamilyId = clean(row.sourceFamilyId);
    const marketplaceAspects = normalizeStringMap({
      ...(row.marketplaceAspects ?? {}),
      ...(clean(row.autographAuthentication) === ''
        ? {}
        : { 'Autograph Authentication': clean(row.autographAuthentication) }),
    });

    if (sourceTargetId !== clean(options.expectedTargetId)) {
      errors.push({
        code: 'mixed-target',
        detail: 'The row target does not match the expected target.',
        fieldPath: `/rows/${rowIndex}/sourceTargetId`,
        ...diagnosticSourceIds(row),
        ...(sourceItemId !== '' ? { sourceItemIds: [sourceItemId] } : {}),
      });
      continue;
    }

    const missingRequired = [
      ['sourceItemId', sourceItemId],
      ['sourceSubsetId', sourceSubsetId],
      ['subset', subset],
    ]
      .filter(([, value]) => value === '')
      .map(([field]) => field);
    const hasIdentityEvidence = number !== '' || name !== '' || (players?.length ?? 0) > 0;
    if (missingRequired.length > 0 || !hasIdentityEvidence) {
      const missing = [
        ...missingRequired,
        ...(!hasIdentityEvidence ? ['number'] : []),
        ...(!hasIdentityEvidence ? ['name'] : []),
      ];
      errors.push({
        code: 'missing-required-field',
        detail: 'A required row field or identity signal is missing.',
        fieldPath: `/rows/${rowIndex}/${missing[0]}`,
        ...diagnosticSourceIds(row),
        ...(sourceItemId !== '' ? { sourceItemIds: [sourceItemId] } : {}),
      });
      continue;
    }
    if (number === '') {
      warnings.push({
        code: 'missing-card-number',
        detail: `source item ${sourceItemId} has no printed card number`,
        sourceItemId,
      });
    }
    if (name === '') {
      warnings.push({
        code: 'missing-card-name',
        detail: `source item ${sourceItemId} has no sourced display name`,
        sourceItemId,
      });
    }
    if (
      serialTo !== undefined &&
      (!Number.isInteger(serialTo) || serialTo < 1 || serialTo > 1_000_000)
    ) {
      errors.push({
        code: 'invalid-roster-attribute',
        detail: 'serialTo must be an integer from 1 to 1000000.',
        fieldPath: `/rows/${rowIndex}/serialTo`,
        ...diagnosticSourceIds(row),
        ...(sourceItemId !== '' ? { sourceItemIds: [sourceItemId] } : {}),
      });
      continue;
    }

    const entry: ChecklistRosterSnapshotEntry = {
      naturalKey: checklistRosterNaturalKey(subset, number, sourceItemId),
      subset,
      ...(subsetTitleSafe !== '' ? { subsetTitleSafe } : {}),
      number,
      name,
      ...(nameTitleSafe !== '' ? { nameTitleSafe } : {}),
      ...(team !== '' ? { team } : {}),
      sourceItemId,
      sourceSubsetId,
      ...(sourceFile !== '' ? { sourceFile } : {}),
      ...(marketplaceAspects === undefined ? {} : { marketplaceAspects }),
      ...(rarity !== '' ? { rarity } : {}),
      ...(finish !== '' ? { finish } : {}),
      ...(autographAuthentication !== '' ? { autographAuthentication } : {}),
      ...(autographAuthenticationNumber !== '' ? { autographAuthenticationNumber } : {}),
      ...(autographFormat !== '' ? { autographFormat } : {}),
      ...(illustrator !== '' ? { illustrator } : {}),
      ...(featuredPerson !== '' ? { featuredPerson } : {}),
      ...(eventTournament !== '' ? { eventTournament } : {}),
      ...(row.ccgAttributes === undefined ? {} : { ccgAttributes: row.ccgAttributes }),
      ...(ccgAttributesRaw !== '' ? { ccgAttributesRaw } : {}),
      ...(parallelName !== '' ? { parallelName } : {}),
      ...(parallelNameTitleSafe !== '' ? { parallelNameTitleSafe } : {}),
      ...(serialTo !== undefined ? { serialTo } : {}),
      ...(cardType !== '' ? { cardType } : {}),
      ...(players === undefined || players.length === 0 ? {} : { players }),
      ...(notes !== '' ? { notes } : {}),
      rookie: row.rookie === true,
      shortPrint: row.shortPrint === true,
      ...(shortPrintNote !== '' ? { shortPrintNote } : {}),
      autograph: row.autograph === true,
      memorabilia: row.memorabilia === true,
      ...(sourceFamilyId !== '' ? { sourceFamilyId } : {}),
    };
    entries.push(entry);
    originalRowIndexes.set(entry, rowIndex);
    const sameKey = entriesByKey.get(entry.naturalKey) ?? [];
    sameKey.push(entry);
    entriesByKey.set(entry.naturalKey, sameKey);
    const sameSourceItemId = entriesBySourceItemId.get(entry.sourceItemId) ?? [];
    sameSourceItemId.push(entry);
    entriesBySourceItemId.set(entry.sourceItemId, sameSourceItemId);
  }

  for (const [sourceItemId, duplicates] of entriesBySourceItemId) {
    if (duplicates.length > 1) {
      errors.push({
        code: 'duplicate-source-item-id',
        fieldPath: '/rows',
        ...diagnosticSourceIds({ sourceTargetId: options.expectedTargetId, sourceItemId }),
        detail: `source item ID ${sourceItemId} appears ${duplicates.length} times in target ${clean(options.expectedTargetId)}`,
        sourceItemIds: [sourceItemId],
      });
    }
  }

  for (const [naturalKey, duplicates] of entriesByKey) {
    if (duplicates.length > 1) {
      errors.push({
        code: 'duplicate-natural-key',
        fieldPath: '/rows',
        detail: `multiple source rows normalize to ${naturalKey}`,
        sourceItemIds: duplicates.map((entry) => entry.sourceItemId).sort(),
      });
    }
  }

  if (options.expectedRosterCount !== undefined && rows.length !== options.expectedRosterCount) {
    errors.push({
      code: 'unexpected-roster-count',
      fieldPath: '/expectedRosterCount',
      detail: `expected ${options.expectedRosterCount} source rows but received ${rows.length}`,
    });
  }

  for (const [definitionIndex, row] of (options.parallelDefinitions ?? []).entries()) {
    const sourceTargetId = clean(row.sourceTargetId);
    const sourceSubsetId = clean(row.sourceSubsetId);
    const name = clean(row.name);
    const nameTitleSafe = clean(row.nameTitleSafe);
    const printRunText = clean(row.printRunText);
    const sourceFile = clean(row.sourceFile);
    const notes = clean(row.notes);
    const sourceFamilyId = clean(row.sourceFamilyId);
    const serialTo = canonicalSerialTo(row.serialTo, schemaVersion);
    if (sourceTargetId !== clean(options.expectedTargetId)) {
      errors.push({
        code: 'mixed-target',
        detail: 'The definition target does not match the expected target.',
        fieldPath: `/parallelDefinitions/${definitionIndex}/sourceTargetId`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    if (sourceSubsetId === '' || name === '') {
      errors.push({
        code: 'invalid-parallel-definition',
        detail: 'A required definition field is empty.',
        fieldPath: `/parallelDefinitions/${definitionIndex}/${sourceSubsetId === '' ? 'sourceSubsetId' : 'name'}`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    if (
      serialTo !== undefined &&
      (!Number.isInteger(serialTo) || serialTo < 1 || serialTo > 1_000_000)
    ) {
      errors.push({
        code: 'invalid-parallel-definition',
        detail: 'serialTo must be an integer from 1 to 1000000.',
        fieldPath: `/parallelDefinitions/${definitionIndex}/serialTo`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    const applicability = normalizeApplicability(row.applicability);
    if (applicability === undefined) {
      errors.push({
        code: 'invalid-parallel-definition',
        ...applicabilityDiagnostic(
          row.applicability,
          `/parallelDefinitions/${definitionIndex}/applicability`,
        ),
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    const definitionId = `${clean(product.sourceSystem)}#${sourceTargetId}#${sourceSubsetId}`;
    const definition: ChecklistParallelDefinition = {
      definitionId,
      name,
      ...(nameTitleSafe !== '' ? { nameTitleSafe } : {}),
      ...(serialTo !== undefined ? { serialTo } : {}),
      ...(printRunText !== '' ? { printRunText } : {}),
      applicability,
      applicabilityConfidence: row.applicabilityConfidence,
      autograph: row.autograph,
      memorabilia: row.memorabilia,
      shortPrint: row.shortPrint,
      ...(notes !== '' ? { notes } : {}),
      source: {
        system: product.sourceSystem,
        targetId: sourceTargetId,
        subsetId: sourceSubsetId,
        snapshotFingerprint: '',
        ...(sourceFamilyId !== '' ? { familyId: sourceFamilyId } : {}),
        ...(sourceFile !== '' ? { sourceFile } : {}),
      },
    };
    definitions.push(definition);
    const sameId = definitionsById.get(definitionId) ?? [];
    sameId.push(definition);
    definitionsById.set(definitionId, sameId);
  }

  for (const [definitionId, duplicates] of definitionsById) {
    if (duplicates.length > 1) {
      errors.push({
        code: 'duplicate-parallel-definition',
        fieldPath: '/parallelDefinitions',
        detail: `multiple source rows use parallel definition ${definitionId}`,
      });
    }
  }

  const rawDefinitionCount = options.parallelDefinitions?.length ?? 0;
  if (
    options.expectedParallelDefinitionCount !== undefined &&
    rawDefinitionCount !== options.expectedParallelDefinitionCount
  ) {
    errors.push({
      code: 'unexpected-parallel-definition-count',
      fieldPath: '/expectedParallelDefinitionCount',
      detail: `expected ${options.expectedParallelDefinitionCount} parallel definitions but received ${rawDefinitionCount}`,
    });
  }

  for (const [definitionIndex, row] of (options.subsetDefinitions ?? []).entries()) {
    const sourceTargetId = clean(row.sourceTargetId);
    const sourceSubsetId = clean(row.sourceSubsetId);
    const name = clean(row.name);
    const nameTitleSafe = clean(row.nameTitleSafe);
    const printRunText = clean(row.printRunText);
    const sourceFile = clean(row.sourceFile);
    const notes = clean(row.notes);
    const sourceFamilyId = clean(row.sourceFamilyId);
    const serialTo = canonicalSerialTo(row.serialTo, schemaVersion);
    if (sourceTargetId !== clean(options.expectedTargetId)) {
      errors.push({
        code: 'mixed-target',
        detail: 'The definition target does not match the expected target.',
        fieldPath: `/subsetDefinitions/${definitionIndex}/sourceTargetId`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    if (sourceSubsetId === '' || name === '') {
      errors.push({
        code: 'invalid-subset-definition',
        detail: 'A required definition field is empty.',
        fieldPath: `/subsetDefinitions/${definitionIndex}/${sourceSubsetId === '' ? 'sourceSubsetId' : 'name'}`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    if (
      serialTo !== undefined &&
      (!Number.isInteger(serialTo) || serialTo < 1 || serialTo > 1_000_000)
    ) {
      errors.push({
        code: 'invalid-subset-definition',
        detail: 'serialTo must be an integer from 1 to 1000000.',
        fieldPath: `/subsetDefinitions/${definitionIndex}/serialTo`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    if (row.noPlainVersion !== undefined && typeof row.noPlainVersion !== 'boolean') {
      errors.push({
        code: 'invalid-subset-definition',
        detail: 'noPlainVersion must be a boolean when present.',
        fieldPath: `/subsetDefinitions/${definitionIndex}/noPlainVersion`,
        ...diagnosticSourceIds(row, true),
      });
      continue;
    }
    const definitionId = `${clean(product.sourceSystem)}#${sourceTargetId}#${sourceSubsetId}`;
    const definition: ChecklistSubsetDefinition = {
      definitionId,
      name,
      ...(nameTitleSafe !== '' ? { nameTitleSafe } : {}),
      ...(serialTo !== undefined ? { serialTo } : {}),
      ...(printRunText !== '' ? { printRunText } : {}),
      applicabilityConfidence: row.applicabilityConfidence,
      autograph: row.autograph,
      memorabilia: row.memorabilia,
      shortPrint: row.shortPrint,
      ...(row.noPlainVersion === true ? { noPlainVersion: true } : {}),
      ...(notes !== '' ? { notes } : {}),
      source: {
        system: product.sourceSystem,
        targetId: sourceTargetId,
        subsetId: sourceSubsetId,
        snapshotFingerprint: '',
        ...(sourceFamilyId !== '' ? { familyId: sourceFamilyId } : {}),
        ...(sourceFile !== '' ? { sourceFile } : {}),
      },
    };
    subsetDefinitions.push(definition);
    const sameId = subsetDefinitionsById.get(definitionId) ?? [];
    sameId.push(definition);
    subsetDefinitionsById.set(definitionId, sameId);
  }

  for (const [definitionId, duplicates] of subsetDefinitionsById) {
    if (duplicates.length > 1) {
      errors.push({
        code: 'duplicate-subset-definition',
        fieldPath: '/subsetDefinitions',
        detail: `multiple source rows use subset definition ${definitionId}`,
      });
    }
    if (definitionsById.has(definitionId)) {
      errors.push({
        code: 'misclassified-subset-definition',
        fieldPath: '/subsetDefinitions',
        detail: `definition ${definitionId} appears in both subset and parallel definitions`,
      });
    }
  }

  const rawSubsetDefinitionCount = options.subsetDefinitions?.length ?? 0;
  if (
    options.expectedSubsetDefinitionCount !== undefined &&
    rawSubsetDefinitionCount !== options.expectedSubsetDefinitionCount
  ) {
    errors.push({
      code: 'unexpected-subset-definition-count',
      fieldPath: '/expectedSubsetDefinitionCount',
      detail: `expected ${options.expectedSubsetDefinitionCount} subset definitions but received ${rawSubsetDefinitionCount}`,
    });
  }

  if (options.subsetDefinitions !== undefined) {
    for (const entry of entries) {
      const definitionId = `${clean(product.sourceSystem)}#${clean(options.expectedTargetId)}#${entry.sourceSubsetId}`;
      const inParallel = definitionsById.has(definitionId);
      const inSubset = subsetDefinitionsById.has(definitionId);
      if (!inParallel && !inSubset) {
        errors.push({
          code: 'unresolved-subset-definition',
          fieldPath: `/rows/${originalRowIndexes.get(entry)}/sourceSubsetId`,
          ...diagnosticSourceIds({
            sourceTargetId: options.expectedTargetId,
            sourceItemId: entry.sourceItemId,
            sourceSubsetId: entry.sourceSubsetId,
          }),
          detail: `source item ${entry.sourceItemId} references missing definition ${entry.sourceSubsetId}`,
          sourceItemIds: [entry.sourceItemId],
        });
      } else if (
        (options.schemaVersion ?? 1) >= 7
          ? entry.parallelName !== undefined &&
            !inParallel &&
            !subsetDefinitionsById
              .get(definitionId)
              ?.some((definition) => isChecklistPlaceholderLabel(definition.name))
          : (entry.parallelName !== undefined) !== inParallel
      ) {
        errors.push({
          code: 'misclassified-subset-definition',
          fieldPath: `/rows/${originalRowIndexes.get(entry)}/sourceSubsetId`,
          ...diagnosticSourceIds({
            sourceTargetId: options.expectedTargetId,
            sourceItemId: entry.sourceItemId,
            sourceSubsetId: entry.sourceSubsetId,
          }),
          detail: `source item ${entry.sourceItemId} resolves ${entry.sourceSubsetId} to the wrong definition type`,
          sourceItemIds: [entry.sourceItemId],
        });
      }
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  entries.sort(
    (left, right) =>
      left.naturalKey.localeCompare(right.naturalKey) ||
      left.sourceItemId.localeCompare(right.sourceItemId),
  );
  definitions.sort((left, right) => left.definitionId.localeCompare(right.definitionId));
  subsetDefinitions.sort((left, right) => left.definitionId.localeCompare(right.definitionId));
  const marketplace = marketplaceProfile(product);
  const canonical = [
    product.sourceSystem,
    clean(product.sourceTargetId),
    clean(product.category),
    clean(product.year),
    sourceBrand,
    clean(product.product),
    clean(product.categoryType),
    clean(product.game),
    clean(product.franchise),
    clean(product.tvShow),
    clean(product.movie),
    clean(product.genre),
    manufacturer,
    ...(sourceBrandTitleSafe !== '' || productTitleSafe !== ''
      ? ['schema-v7-title-safe', sourceBrandTitleSafe, productTitleSafe]
      : []),
    ...(marketplace === undefined
      ? []
      : [
          [
            marketplace.marketplace,
            marketplace.inventoryType,
            marketplace.categoryType,
            marketplace.categoryId,
            canonicalStringMap(marketplace.aspects),
          ]
            .map(segment)
            .join(''),
        ]),
    ...entries.map((entry) => canonicalEntry(entry)),
    ...definitions.map((definition) => canonicalParallelDefinition(definition)),
    ...subsetDefinitions.map((definition) => canonicalSubsetDefinition(definition)),
  ]
    .map(segment)
    .join('');
  const snapshotFingerprint = fingerprint(canonical);
  const fingerprintedDefinitions = definitions.map((definition) => ({
    ...definition,
    source: { ...definition.source, snapshotFingerprint },
  }));
  const fingerprintedSubsetDefinitions = subsetDefinitions.map((definition) => ({
    ...definition,
    source: { ...definition.source, snapshotFingerprint },
  }));

  return {
    ok: true,
    warnings: warnings.sort(
      (left, right) =>
        left.sourceItemId.localeCompare(right.sourceItemId) || left.code.localeCompare(right.code),
    ),
    snapshot: {
      schemaVersion,
      ...(options.extractedAt !== undefined ? { extractedAt: options.extractedAt } : {}),
      ...(options.sourceHighWater !== undefined
        ? { sourceHighWater: options.sourceHighWater }
        : {}),
      sourceSystem: product.sourceSystem,
      sourceTargetId: clean(product.sourceTargetId),
      category: clean(product.category),
      year: clean(product.year),
      brand: canonicalBrand,
      product: clean(product.product),
      sourceBrand,
      ...(sourceBrandTitleSafe !== '' ? { sourceBrandTitleSafe } : {}),
      ...(productTitleSafe !== '' ? { productTitleSafe } : {}),
      ...(manufacturer !== '' ? { manufacturer } : {}),
      ...(product.categoryType !== undefined ? { sourceCategoryType: product.categoryType } : {}),
      ...(clean(product.franchise) !== '' ? { franchise: clean(product.franchise) } : {}),
      ...(clean(product.tvShow) !== '' ? { tvShow: clean(product.tvShow) } : {}),
      ...(clean(product.movie) !== '' ? { movie: clean(product.movie) } : {}),
      ...(clean(product.genre) !== '' ? { genre: clean(product.genre) } : {}),
      ...(clean(product.game) !== '' ? { game: clean(product.game) } : {}),
      fingerprint: snapshotFingerprint,
      rosterCount: entries.length,
      roster: entries,
      parallelDefinitionCount: fingerprintedDefinitions.length,
      parallelDefinitions: fingerprintedDefinitions,
      subsetDefinitionCount: fingerprintedSubsetDefinitions.length,
      subsetDefinitions: fingerprintedSubsetDefinitions,
      ...(marketplace === undefined ? {} : { marketplace }),
    },
  };
}

/** Only bounded decimal source identifiers may cross the diagnostic boundary. */
function diagnosticSourceIds(
  value: { sourceTargetId?: string; sourceItemId?: string; sourceSubsetId?: string },
  definition = false,
): Pick<
  ChecklistSnapshotBuildError,
  'sourceTargetId' | 'sourceItemId' | 'sourceSubsetId' | 'definitionId'
> {
  const result: Pick<
    ChecklistSnapshotBuildError,
    'sourceTargetId' | 'sourceItemId' | 'sourceSubsetId' | 'definitionId'
  > = {};
  for (const key of ['sourceTargetId', 'sourceItemId', 'sourceSubsetId'] as const) {
    const id = value[key];
    if (typeof id === 'string' && /^[0-9]{1,64}$/.test(id)) result[key] = id;
  }
  if (definition && result.sourceTargetId && result.sourceSubsetId)
    result.definitionId = `azure-sql-ebay#${result.sourceTargetId}#${result.sourceSubsetId}`;
  return result;
}

function applicabilityDiagnostic(
  value: unknown,
  path: string,
): Pick<ChecklistSnapshotBuildError, 'detail' | 'fieldPath'> {
  const error = (suffix: string, detail: string) => ({ fieldPath: `${path}${suffix}`, detail });
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    return error('', 'applicability must be an object.');
  const rule = value as Record<string, unknown>;
  if (!['unknown', 'all-cards-in-subset', 'explicit-cards'].includes(String(rule['kind'])))
    return error(
      '/kind',
      'applicability kind must be unknown, all-cards-in-subset or explicit-cards.',
    );
  if (
    (rule['subset'] !== undefined && typeof rule['subset'] !== 'string') ||
    (rule['kind'] !== 'unknown' &&
      (typeof rule['subset'] !== 'string' || rule['subset'].trim() === ''))
  )
    return error('/subset', 'applicability subset must be a nonempty string for a known rule.');
  if (!Array.isArray(rule['numbers']) || rule['numbers'].length === 0)
    return error('/numbers', 'explicit-cards numbers must be a nonempty array of printed strings.');
  const index = rule['numbers'].findIndex(
    (number) => typeof number !== 'string' || number.trim() === '',
  );
  return error(`/numbers/${index}`, 'Each printed number must be a nonempty string.');
}

/** Validate untrusted input before touching printed-number membership. */
function normalizeApplicability(value: unknown): ParallelApplicability | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const rule = value as Record<string, unknown>;
  if (rule['subset'] !== undefined && typeof rule['subset'] !== 'string') return undefined;
  const subset = typeof rule['subset'] === 'string' ? rule['subset'].trim() : '';
  if (rule['kind'] === 'unknown') {
    return { kind: 'unknown', ...(subset !== '' ? { subset } : {}) };
  }
  if (subset === '') return undefined;
  if (rule['kind'] === 'all-cards-in-subset') {
    return { kind: 'all-cards-in-subset', subset };
  }
  if (rule['kind'] !== 'explicit-cards' || !Array.isArray(rule['numbers'])) return undefined;
  if (!rule['numbers'].every((number) => typeof number === 'string' && number.trim() !== '')) {
    return undefined;
  }
  const numbers = [...new Set((rule['numbers'] as string[]).map(clean))].sort((left, right) =>
    left.localeCompare(right),
  );
  if (numbers.length === 0) return undefined;
  return { kind: 'explicit-cards', subset, numbers };
}

function sameEntry(
  left: ChecklistRosterSnapshotEntry,
  right: ChecklistRosterSnapshotEntry,
): boolean {
  return canonicalEntry(left) === canonicalEntry(right);
}

export function diffChecklistSourceSnapshots(
  existing: ChecklistSourceSnapshot | undefined,
  proposed: ChecklistSourceSnapshot,
): ChecklistSnapshotDiff {
  const before = new Map(existing?.roster.map((entry) => [entry.naturalKey, entry]) ?? []);
  const after = new Map(proposed.roster.map((entry) => [entry.naturalKey, entry]));
  const result: ChecklistSnapshotDiff = {
    adds: [],
    updates: [],
    unchanged: [],
    sourceMissing: [],
  };

  for (const [naturalKey, next] of after) {
    const current = before.get(naturalKey);
    if (current === undefined) {
      result.adds.push({ kind: 'add', naturalKey, after: next });
    } else if (sameEntry(current, next)) {
      result.unchanged.push({
        kind: 'unchanged',
        naturalKey,
        before: current,
        after: next,
      });
    } else {
      result.updates.push({
        kind: 'update',
        naturalKey,
        before: current,
        after: next,
      });
    }
  }

  for (const [naturalKey, current] of before) {
    if (!after.has(naturalKey)) {
      result.sourceMissing.push({
        kind: 'source-missing',
        naturalKey,
        before: current,
      });
    }
  }

  return result;
}

export function diffChecklistParallelDefinitions(
  existing: readonly ChecklistParallelDefinition[],
  proposed: readonly ChecklistParallelDefinition[],
): ChecklistParallelDefinitionDiff {
  const before = new Map(existing.map((definition) => [definition.definitionId, definition]));
  const after = new Map(proposed.map((definition) => [definition.definitionId, definition]));
  const result: ChecklistParallelDefinitionDiff = {
    adds: [],
    updates: [],
    unchanged: [],
    sourceMissing: [],
  };
  for (const [definitionId, next] of after) {
    const current = before.get(definitionId);
    if (current === undefined) {
      result.adds.push({ kind: 'add', definitionId, after: next });
    } else if (canonicalParallelDefinition(current) === canonicalParallelDefinition(next)) {
      result.unchanged.push({
        kind: 'unchanged',
        definitionId,
        before: current,
        after: next,
      });
    } else {
      result.updates.push({ kind: 'update', definitionId, before: current, after: next });
    }
  }
  for (const [definitionId, current] of before) {
    if (!after.has(definitionId)) {
      result.sourceMissing.push({ kind: 'source-missing', definitionId, before: current });
    }
  }
  return result;
}

export function diffChecklistSubsetDefinitions(
  existing: readonly ChecklistSubsetDefinition[],
  proposed: readonly ChecklistSubsetDefinition[],
): ChecklistSubsetDefinitionDiff {
  const before = new Map(existing.map((definition) => [definition.definitionId, definition]));
  const after = new Map(proposed.map((definition) => [definition.definitionId, definition]));
  const result: ChecklistSubsetDefinitionDiff = {
    adds: [],
    updates: [],
    unchanged: [],
    sourceMissing: [],
  };
  for (const [definitionId, next] of after) {
    const current = before.get(definitionId);
    if (current === undefined) {
      result.adds.push({ kind: 'add', definitionId, after: next });
    } else if (canonicalSubsetDefinition(current) === canonicalSubsetDefinition(next)) {
      result.unchanged.push({
        kind: 'unchanged',
        definitionId,
        before: current,
        after: next,
      });
    } else {
      result.updates.push({ kind: 'update', definitionId, before: current, after: next });
    }
  }
  for (const [definitionId, current] of before) {
    if (!after.has(definitionId)) {
      result.sourceMissing.push({ kind: 'source-missing', definitionId, before: current });
    }
  }
  return result;
}
